import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { useDispatch, useSelector } from 'react-redux'
import { Stack, Grid, Box, Divider, Typography, Button } from '@mui/material'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { useForm, useFormState } from 'react-hook-form'
import Input from 'components/generic/form/Input'
import BasicDataGrid from 'components/generic/datagrid'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import FormModalActionBar from 'components/generic/modals/form-modal/FormModalActionBar'
import MaterialAutocomplete from 'components/generic/form/AsyncAutocomplete/MaterialAutocomplete'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import Loading from 'components/generic/Loading'
import { showFormModal, setSnackBar } from 'state/ui'
import FORM_MODALS from 'utils/ui/modals'
import { useLocation } from 'react-router-dom'
import rem from 'utils/rem'
import {
  useCreatePackingListMutation,
  useGetPackingListByIdQuery,
  useUpdatePackingListMutation,
  useVoidPackingListMutation,
} from 'services/packingList'
import { useGetFinishedGoodsQuery } from 'services/finishedGoods'
import { formOptions } from 'utils/validation/packingList'
import PrintDialog from 'components/generic/printable/PrintDialog'
import PrintablePackingListOrBoL from 'components/generic/printable/PrintablePackingListOrBoL'
import { COMMON_STATUSES, CHURN_ZERO_EVENTS as cz, MODAL_VIEW_MODES } from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'

const formatData = (data, ids, net) => ({
  name: data.materialName,
  finishedGoodsIds: ids,
  net,
  notes: [
    {
      name: data.extraNotes,
    },
  ],
})

const NewPackingListContainer = ({ onFormStateChange, onHideModal }) => {
  const { enablePullApartFeatures } = useSelector(state => state.featureFlags?.variables || {})

  const { handleSubmit, control, reset, setValue, getValues } = useForm(formOptions)
  const { errors } = useFormState({ control })
  const dispatch = useDispatch()
  const apiRef = useGridApiRef()
  const { pathname } = useLocation()
  const cleanedPathname = pathname[pathname.length - 1] === '/' ? pathname.slice(0, -1) : pathname
  const packingListId = cleanedPathname.split('/').pop()
  const isUpdate = !!packingListId && packingListId !== 'new'
  const date = new Date()
  const dateFormatted = dateFormatter(date)
  const [showPrintPLModal, setShowPrintPLModal] = useState(false)
  const handleOpenPrintDialog = () => setShowPrintPLModal(true)
  const handleClosePrintDialog = () => setShowPrintPLModal(false)

  const { defaultYardUm } = useSelector(state => state.settings)

  const [createPackingList, { isLoading: isCreating }] = useCreatePackingListMutation()
  const { data: packingListItem, isLoading: isGettingPackingListItem } = useGetPackingListByIdQuery(
    { packingListId },
    { refetchOnMountOrArgChange: true, skip: !isUpdate },
  )
  const [
    // updatePackingList,
    { isLoading: isUpdating },
  ] = useUpdatePackingListMutation()
  const { data: finishedGoodsData, isLoading: isGettingFinishedGoods } = useGetFinishedGoodsQuery({
    filter: JSON.stringify({
      statuses: [COMMON_STATUSES.AVAILABLE.id],
    }),
  })
  const [voidPackingList, { isLoading: isDeletingPackingList }] = useVoidPackingListMutation()
  const isLoading =
    (isGettingPackingListItem && isGettingFinishedGoods) || isCreating || isDeletingPackingList || isUpdating
  const title = isUpdate ? `Packing List #${packingListItem?.tag || ''}` : 'Create New Packing List'

  const [finishedGoodsList, setFinishedGoodsList] = useState([])
  const [finishedGoodsIds, setFinishedGoodsIds] = useState([])
  const [showVoidModal, setShowVoidModal] = useState(false)

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  useEffect(() => {
    if (packingListItem) {
      setFinishedGoodsIds(packingListItem?.finishedGoodsIds)
      setFinishedGoodsList(
        packingListItem?.finishedGoods?.map(good => ({
          id: good.id,
          fgId: good.tag,
          materialName: good.materialType?.commonName,
          type: good.type,
          itemCount: good.itemCount,
          gross: good.weight?.gross,
          tare: good.weight?.tare,
          net: good.weight?.net,
        })) || [],
      )
      reset({
        materialName: packingListItem?.finishedGoods?.[0]?.materialType?.commonName || '',
        extraNotes: packingListItem?.notes?.[0]?.name || '',
      })
    }
  }, [packingListItem])

  const fgColumns = useMemo(() => {
    const columns = [
      {
        field: 'fgId',
        headerName: 'FG ID',
        flex: 1,
        editable: false,
        sortable: false,
      },
      {
        field: 'materialName',
        headerName: 'Material Name',
        flex: 1,
        editable: false,
        sortable: false,
      },
      {
        field: 'type',
        headerName: 'Type',
        editable: false,
        sortable: false,
      },
      {
        field: 'itemCount',
        headerName: 'Count',
        editable: false,
        sortable: false,
      },
      {
        field: 'gross',
        headerName: 'Gross',
        type: 'number',
        editable: false,
        headerAlign: 'center',
        sortable: false,
      },
      {
        field: 'tare',
        headerName: 'Tare',
        type: 'number',
        editable: false,
        headerAlign: 'center',
        sortable: false,
      },
      {
        field: 'net',
        headerName: 'Net',
        type: 'number',
        valueGetter: params => {
          const { gross = 0, tare = 0 } = params.row
          return gross - tare
        },
        sortable: false,
      },
    ]
    const filteredColumns = columns.filter(column => {
      if (!enablePullApartFeatures && column.field === 'itemCount') return false
      return true
    })
    return filteredColumns
  }, [enablePullApartFeatures])

  const handleError = error => {
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: error?.data?.error || 'There was an error voiding the packing list',
      }),
    )
  }

  const handleSuccess = () =>
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: 'Packing list successfully voided',
      }),
    )

  const filteredFinishedGoodsList = finishedGoodsList.filter(fg => finishedGoodsIds?.includes(fg.id))

  const totalWeight = filteredFinishedGoodsList.reduce((acc, next) => acc + Number(next?.net ? next?.net : 0), 0)

  const onSubmit = async data => {
    const packingList = formatData(data, finishedGoodsIds, totalWeight)
    if (!isUpdate) {
      await createPackingList({ packingListRequestDto: packingList })
        .unwrap()
        .then(response => {
          reset(data)
          trackEvent({
            eventName: cz.CREATE_PACKING_LIST,
            quantity: response?.finishedGoods?.length,
          })
          dispatch(
            showFormModal({
              formModalName: FORM_MODALS.PACKING_LIST_CREATED,
              formModalProps: {
                ...response,
                finishedGoods: filteredFinishedGoodsList,
              },
            }),
          )
        })
        .catch(error => {
          trackEvent({
            eventName: cz.CREATE_PACKING_LIST,
            errorMsg: error,
          })
          handleError(error)
        })
    }
  }

  const onClickVoid = async () => {
    setShowVoidModal(false)

    await voidPackingList({ packingListId })
      .unwrap()
      .then(() => {
        handleSuccess()
        onHideModal()
      })
      .catch(error => handleError(error))
  }

  return (
    <>
      <AlertModal
        open={showVoidModal}
        modalType="void"
        title="Void This Packing List"
        question="Are you sure you want to void this packing list?"
        description={`Packing list #${packingListId} will be voided and all data will be lost.`}
        confirmLabel="YES, CONFIRM VOID"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowVoidModal(false)
        }}
        onSubmit={onClickVoid}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        {isLoading && <Loading />}
        <Stack height="100vh">
          <FormModalHeading title={title} subtitle={dateFormatted} onBack={onHideModal} />

          <Stack p={5} flexGrow={1} overflow="scroll">
            <Box mt={3}>
              <Typography variant="h6" color="primary">
                Add Finished Goods
              </Typography>
              <Box py={1}>
                <Divider />
              </Box>
              <Grid container mt={1} spacing={2}>
                <Grid item md={6} xs={12}>
                  <MaterialAutocomplete
                    label="Material"
                    placeholder="Search by Material Name or Code"
                    variant="outlined"
                    defaultValue={{
                      commonName: packingListItem?.finishedGoods?.[0]?.materialType?.commonName,
                      code: packingListItem?.finishedGoods?.[0]?.materialType?.code,
                      materialTypeId: packingListItem?.finishedGoods?.[0]?.materialType?.id,
                    }}
                    disabled={isUpdate}
                    TextFieldProps={{
                      error: !!errors.materialName,
                      helperText: errors.materialName?.message,
                    }}
                    onChange={(material, reason) => {
                      if (reason !== undefined) {
                        setValue('materialName', material?.commonName)
                        setFinishedGoodsList(
                          (finishedGoodsData?.Items || [])
                            .filter(good => good?.materialTypeId === material?.id)
                            .map(good => ({
                              id: good.id,
                              fgId: good.tag,
                              materialName: good.materialType?.commonName,
                              type: good.type,
                              itemCount: good.itemCount,
                              gross: good.weight?.gross,
                              tare: good.weight?.tare,
                              net: good.weight?.net,
                            })),
                        )
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            <Stack direction="row" mt={2} alignItems="center" justifyContent="space-between">
              <Stack alignItems="center" direction="row">
                <Typography fontWeight="bold" fontSize={rem(18)}>
                  Total Weight
                </Typography>
                <Typography ml={2}>{`${totalWeight} ${defaultYardUm}`}</Typography>
              </Stack>
              <Typography color="disabled">{finishedGoodsIds?.length} Selected Finished Goods</Typography>
            </Stack>

            <Box mt={2}>
              <BasicDataGrid
                density="compact"
                height="auto"
                rows={finishedGoodsList}
                columns={fgColumns}
                apiRef={apiRef}
                editMode="row"
                checkboxSelection
                disableColumnMenu
                hideFooter
                onSelectionModelChange={ids => {
                  setFinishedGoodsIds(ids)
                }}
                selectionModel={finishedGoodsIds}
              />
            </Box>

            <Box mt={5} mb={3}>
              <Typography variant="h6" color="primary">
                Additional Notes
              </Typography>
              <Box py={1}>
                <Divider />
              </Box>
              <Grid container mt={1}>
                <Grid item xs={12}>
                  <Input name="extraNotes" label="Extra Notes" defaultValue="" control={control} disabled={isUpdate} />
                </Grid>
              </Grid>
            </Box>

            {isUpdate && (
              <Box mt={3}>
                <Button onClick={() => handleOpenPrintDialog(true)} variant="outlined">
                  Print Packing List
                </Button>
              </Box>
            )}
          </Stack>

          {showPrintPLModal && (
            <PrintDialog
              title={`Packing List #${packingListItem?.tag}`}
              open={showPrintPLModal}
              onClose={handleClosePrintDialog}
              showLogoAddress
            >
              <PrintablePackingListOrBoL
                overall={{ gross: 0, tare: 0, net: packingListItem?.net }}
                finishedGoods={finishedGoodsList}
                notes={getValues()?.extraNotes}
              />
            </PrintDialog>
          )}

          <FormModalActionBar
            viewMode={isUpdate ? MODAL_VIEW_MODES.VIEW : MODAL_VIEW_MODES.NEW}
            submitActionText={isUpdate ? 'EDIT' : 'Create Packing List'}
            deleteActionText={isUpdate ? 'Delete' : ''}
            onCancel={onHideModal}
            onDelete={() => {
              setShowVoidModal(true)
            }}
            isFormVoidable
          />
        </Stack>
      </form>
    </>
  )
}

NewPackingListContainer.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

NewPackingListContainer.defaultProps = {
  onFormStateChange: noop,
  onHideModal: null,
}

export default NewPackingListContainer
